import { ILoader } from '../../base/loader/iLoader.js';
import type { JsonObject } from '../../app/json/jsonValue.js';
import type { DynamicResults } from '../../model/dynamicResults.js';
import type { PhaseTiming } from '../../base/timing/phaseTiming.js';

/**
 * ILoaderNormal - Loader interface specifically for EngineNormal
 *
 * This is a marker interface (like C# interfaces) - no method bodies.
 * Extends ILoader with Normal-specific getTemplate() return type.
 * Actual implementation is in LoaderNormal and loaderNormal_*.ts partial files.
 *
 * Expected method (in addition to ILoader methods):
 *
 * @method setDynamicResults(dynamicResults) - Sets the DynamicResults instance for the current merge
 * @method getTemplate(appSite, currentTemplateName, appFileOrPlaceholderOrSlotholderName, appView?, flowStack?, configJson?)
 *   Gets a template's HTML content and JSON with AppView and SearchAppSites fallback support.
 *   Returns: {html: string, json: JsonObject|null, resolvedTemplateName: string, resolvedAppSite: string}
 *
 * @method applyDynamicViewHandlers(appSite, appView, currentTemplateName, componentName, componentDepth, componentHtml, componentJson, resolvedAppSite, resolvedComponentName, configJson?)
 *   Applies DynamicView handlers to wrap/modify component HTML. Called by Engine after getTemplate.
 *   Returns: string (modified HTML)
 *
 * IMPORTANT CONTRACT:
 * - currentTemplateName MUST be a clean template name WITHOUT AppSite prefix
 * - Example: "MainContent" NOT "JsonRule5A_MainContent"
 */
export interface GetTemplateResult {
    html: string;
    json: JsonObject | null;
    resolvedTemplateName: string;
    resolvedAppSite: string;
}

export abstract class ILoaderNormal extends ILoader {
    abstract setDynamicResults(dynamicResults: DynamicResults): void;
    abstract getTemplate(appSite: string, currentTemplateName: string, appFileOrPlaceholderOrSlotholderName: string, timing: PhaseTiming, appView?: string | null, flowStack?: string[] | null, configJson?: JsonObject | null): GetTemplateResult;
    abstract applyDynamicViewHandlers(appSite: string, appView: string, appFile: string, componentName: string, componentDepth: number, componentHtml: string, componentJson: JsonObject | null, resolvedAppSite: string, resolvedComponentName: string, configJson: JsonObject | null, timing: PhaseTiming): string;
    abstract getTemplateWithView(appSite: string, currentTemplateName: string, componentName: string, timing: PhaseTiming, appView?: string | null, flowStack?: string[] | null, configJson?: JsonObject | null, clientMode?: boolean): GetTemplateResult;
}
